import { Plus, Trash2, Zap, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { useScopeStore } from '../store'
import { useSettingsStore } from '../../settings/store'
import { HOUR_SECTIONS, COMPLEXITY_OPTIONS } from '../types'
import { calculateAutoHours, calculateTotalHours, groupEntriesBySection } from '../hourCalculator'

export function HoursTab() {
  const {
    scope,
    updateGeneralInfo,
    addScopeHour,
    updateScopeHour,
    removeScopeHour,
    addFeatureRequest,
    updateFeatureRequest,
    removeFeatureRequest,
  } = useScopeStore()
  const { hourSettings } = useSettingsStore()
  const { scopeHours, featureRequests } = scope
  const disabled = scope.finalized

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  // Calculate auto-generated hours
  const autoHours = calculateAutoHours(scope, hourSettings)
  const autoTotal = calculateTotalHours(autoHours)
  const groupedAutoHours = groupEntriesBySection(autoHours)

  // Calculate manual hours with complexity
  const manualTotal = scopeHours.reduce((sum, h) => {
    const multiplier = COMPLEXITY_OPTIONS.find((c) => c.id === h.complexity)?.multiplier ?? 1
    return sum + h.estimatedHours * multiplier
  }, 0)

  const featureHours = featureRequests.reduce((sum, r) => sum + r.hours, 0)
  const grandTotal = autoTotal + manualTotal + featureHours
  const hourlyRate = 250
  const implementationFee = grandTotal * hourlyRate

  // Group manual hours by section
  const hoursBySection = HOUR_SECTIONS.map((section) => ({
    ...section,
    hours: scopeHours.filter((h) => h.section === section.id),
    total: scopeHours
      .filter((h) => h.section === section.id)
      .reduce((sum, h) => {
        const multiplier = COMPLEXITY_OPTIONS.find((c) => c.id === h.complexity)?.multiplier ?? 1
        return sum + h.estimatedHours * multiplier
      }, 0),
  }))

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">Implementation Hours</h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Hours are automatically calculated based on your configuration selections</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="border border-[var(--border-color)] rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1">
            <Zap size={12} className="text-primary" />
            Auto-Generated
          </div>
          <div className="text-2xl font-bold text-[var(--text-primary)]">{autoTotal.toFixed(1)}</div>
        </div>
        <div className="border border-[var(--border-color)] rounded-lg p-4">
          <div className="text-xs text-[var(--text-muted)] mb-1">Manual Entries</div>
          <div className="text-2xl font-bold text-[var(--text-primary)]">{manualTotal.toFixed(1)}</div>
        </div>
        <div className="border border-[var(--border-color)] rounded-lg p-4">
          <div className="text-xs text-[var(--text-muted)] mb-1">Feature Requests</div>
          <div className="text-2xl font-bold text-[var(--text-primary)]">{featureHours.toFixed(1)}</div>
        </div>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
          <div className="text-xs text-primary/80 mb-1">Total Implementation Fee</div>
          <div className="text-2xl font-bold text-primary">
            ${implementationFee.toLocaleString()}
          </div>
          <div className="text-xs text-primary/60 mt-0.5">
            {grandTotal.toFixed(1)} hrs × ${hourlyRate}/hr
          </div>
        </div>
      </div>

      {/* Pre-sales Hours */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">Pre-sales</h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Pre-sales Hours</label>
            <input
              type="number"
              step="0.5"
              min="0"
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={scope.generalInfo.presalesHours || ''}
              onChange={(e) => updateGeneralInfo('presalesHours', Number(e.target.value))}
              disabled={disabled}
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">Optional: estimated pre-sales/discovery hours for this deal.</p>
          </div>
        </div>
      </div>

      {/* Auto-Generated Hours */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">Auto-generated</h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-primary" />
          <span className="text-xs text-[var(--text-muted)] ml-auto">Based on your configuration selections</span>
        </div>

        {autoHours.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)] text-sm">
            No hours generated yet. Configure KYC, AML, Biometrics, or KYB settings to see generated hours.
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(groupedAutoHours).map(([section, entries]) => {
              const sectionTotal = entries.reduce((sum, e) => sum + e.hours, 0)
              const isExpanded = expandedSections[section] ?? false

              return (
                <div key={section} className="bg-[var(--bg-secondary)] rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(section)}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--bg-tertiary)]/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronUp size={14} className="text-[var(--text-muted)]" /> : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
                      <span className="text-sm font-medium text-[var(--text-primary)]">{section}</span>
                      <span className="text-xs text-[var(--text-muted)]">({entries.length} items)</span>
                    </div>
                    <span className="text-sm font-medium text-primary">{sectionTotal.toFixed(1)} hrs</span>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-1">
                      {entries.map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between py-1.5 px-3 text-sm bg-[var(--bg-tertiary)]/30 rounded">
                          <span className="text-[var(--text-secondary)]">{entry.feature}</span>
                          <span className="text-[var(--text-primary)]">{entry.hours.toFixed(1)} hrs</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            <div className="pt-2 border-t border-[var(--border-color)] flex items-center justify-between text-sm">
              <span className="text-[var(--text-primary)] font-medium">Total Auto-Generated</span>
              <span className="text-primary font-bold">{autoTotal.toFixed(1)} hrs</span>
            </div>
          </div>
        )}
      </div>

      {/* Manual Hour Entries */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">Manual entries</h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-[var(--text-muted)]"><span className="text-primary">*</span> Add at least one entry to complete this tab.</p>
          </div>
          <button
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            onClick={() =>
              addScopeHour({
                section: 'general_setup',
                description: '',
                estimatedHours: 1,
                complexity: 'medium',
              })
            }
            disabled={disabled}
          >
            <Plus size={14} />
            Add Entry
          </button>
        </div>

        {scopeHours.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)] text-sm">No manual entries. Click "Add Entry" to add custom hours.</div>
        ) : (
          <div className="space-y-3">
            {scopeHours.map((hour) => (
              <div key={hour.id} className="grid grid-cols-12 gap-3 items-center bg-[var(--bg-secondary)] rounded-lg p-3">
                <div className="col-span-3">
                  <select
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    value={hour.section}
                    onChange={(e) => updateScopeHour(hour.id, { section: e.target.value })}
                    disabled={disabled}
                  >
                    {HOUR_SECTIONS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-4">
                  <input
                    type="text"
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Description"
                    value={hour.description}
                    onChange={(e) => updateScopeHour(hour.id, { description: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Hours"
                    value={hour.estimatedHours || ''}
                    onChange={(e) => updateScopeHour(hour.id, { estimatedHours: Number(e.target.value) })}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-2">
                  <select
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    value={hour.complexity}
                    onChange={(e) =>
                      updateScopeHour(hour.id, { complexity: e.target.value as any })
                    }
                    disabled={disabled}
                  >
                    {COMPLEXITY_OPTIONS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label} ({c.multiplier}x)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    className="p-2 text-[var(--text-muted)] hover:text-danger transition-colors disabled:opacity-50"
                    onClick={() => removeScopeHour(hour.id)}
                    disabled={disabled}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hours by Section Summary */}
        {scopeHours.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
            <h5 className="text-xs font-medium text-[var(--text-secondary)] mb-2">Manual Hours by Section</h5>
            <div className="space-y-1">
              {hoursBySection
                .filter((s) => s.total > 0)
                .map((section) => (
                  <div key={section.id} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--text-secondary)]">{section.label}</span>
                    <span className="text-[var(--text-primary)]">{section.total.toFixed(1)} hrs</span>
                  </div>
                ))}
              <div className="pt-2 border-t border-[var(--border-color)] flex items-center justify-between text-sm">
                <span className="text-[var(--text-primary)] font-medium">Total Manual</span>
                <span className="text-[var(--text-primary)] font-bold">{manualTotal.toFixed(1)} hrs</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feature Requests */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">Feature requests</h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-[var(--text-muted)]">Custom features or modifications requested by the client</p>
          </div>
          <button
            className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50"
            onClick={addFeatureRequest}
            disabled={disabled}
          >
            <Plus size={14} />
            Add Request
          </button>
        </div>

        {featureRequests.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)] text-sm">No feature requests yet.</div>
        ) : (
          <div className="space-y-3">
            {featureRequests.map((request) => (
              <div key={request.id} className="grid grid-cols-12 gap-3 items-center bg-[var(--bg-secondary)] rounded-lg p-3">
                <div className="col-span-3">
                  <input
                    type="text"
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Feature name"
                    value={request.name}
                    onChange={(e) => updateFeatureRequest(request.id, { name: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-6">
                  <input
                    type="text"
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Description"
                    value={request.description}
                    onChange={(e) => updateFeatureRequest(request.id, { description: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="Hours"
                    value={request.hours || ''}
                    onChange={(e) => updateFeatureRequest(request.id, { hours: Number(e.target.value) })}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    className="p-2 text-[var(--text-muted)] hover:text-danger transition-colors disabled:opacity-50"
                    onClick={() => removeFeatureRequest(request.id)}
                    disabled={disabled}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
